import React, { useEffect, useState } from 'react';

import Switcher from 'elements/switcher';

import valueWithPrecision from 'helpers/valueWithPrecision';
import { countMinMaxAmount } from 'helpers';
import { useOrderAmountValidation } from 'hooks';

import { OrderSide, OrderType, InstrumentT } from 'types';

import './CreateBase.scss';
interface Props {
  instrument: InstrumentT | null;
  currency: string;
  walletBalance: string;
  orderSide: OrderSide;
  orderType: OrderType;
  networkFee?: string;
  handlerChangeAmount: Function;
  handlerError?: Function;
  handlerChangeCurrency?: () => void;
  limitPrice?: string;
}

const CreateBase = ({
  instrument,
  orderType,
  orderSide,
  currency = '',
  walletBalance = '',
  networkFee = '',
  limitPrice = '',
  handlerChangeAmount = () => {},
  handlerError = () => {},
  handlerChangeCurrency = () => {},
}: Props) => {
  const [currentCurrency, setCurrentCurrency] = useState('');
  const [precision, setPrecision] = useState({ base: 0, quote: 0 });
  const [maxTradeAmount, setMaxTradeAmount] = useState(0);
  const [focusInput, setFocusInput] = useState(false);

  const validationError: string = useOrderAmountValidation(
    currentCurrency,
    Number(limitPrice),
    Number(walletBalance),
    orderSide,
    orderType,
    currency,
    focusInput
  );

  const isMarket = orderType === OrderType.Market;
  const isLimit = orderType === OrderType.Limit;

  // ---- for switcher
  const [selectedCurrency, setSelectCurrency] = useState('');
  const switchList = [
    { id: 'one', currency: instrument?.base_currency_id },
    { id: 'two', currency: instrument?.quote_currency_id },
  ];
  useEffect(() => {
    if (instrument) {
      setSelectCurrency(instrument.base_currency_id);
    }
  }, [instrument]);
  // ----

  useEffect(() => {
    if (validationError) {
      handlerError(true);
    } else {
      handlerError(false);
    }
  }, [validationError]);

  useEffect(() => {
    if (instrument && Object.keys(instrument).length) {
      setPrecision({
        base: instrument?.base_currency.precision || 0,
        quote: instrument?.quote_currency.precision || 0,
      });
    }
  }, [instrument]);

  useEffect(() => {
    if (instrument) {
      const { maxQuantity } = countMinMaxAmount(Number(limitPrice), instrument, orderType, orderSide);

      setMaxTradeAmount(maxQuantity);
    }
  }, [instrument, orderSide, orderType, limitPrice]);

  useEffect(() => {
    setCurrentCurrency('');
    setSelectCurrency(currency);
  }, [currency]);

  const handlerChange = (e: any) => {
    let inputValue = e.target.value;

    // check if "." is the first input element
    if (inputValue === '.') {
      handlerChangeAmount(String('0.'));
      setCurrentCurrency(String('0.'));
      return;
    }

    const isBaseCurrency = orderSide === OrderSide.Sell;
    const onlyLetters = inputValue.match(/[0-9]*\.?[0-9]*/);
    const precision = isBaseCurrency ? instrument?.base_currency.precision : instrument?.quote_currency.precision;
    let withPrecision = onlyLetters;

    if (onlyLetters[0].split('.')[1] && onlyLetters[0].split('.')[1].length) {
      if (isBaseCurrency) {
        if (onlyLetters[0].split('.')[1].length > (precision || 0)) {
          let cutValue = onlyLetters[0].split('.')[1].split('').slice(0, precision).join('');
          withPrecision = `${onlyLetters[0].split('.')[0]}.${cutValue}`;
        }
      } else {
        if (onlyLetters[0].split('.')[1].length > (precision || 0)) {
          let cutValue = onlyLetters[0].split('.')[1].split('').slice(0, precision).join('');
          withPrecision = `${onlyLetters[0].split('.')[0]}.${cutValue}`;
        }
      }
    }

    setCurrentCurrency(String(withPrecision));
    handlerChangeAmount(String(withPrecision));
  };

  const handlerFocus = () => {
    setFocusInput(true);
  };

  const handlerMaxAmount = () => {
    if (String(walletBalance) === '0') {
      setCurrentCurrency('0');
      handlerChangeAmount('0');
      return;
    }

    let amount: any = String(walletBalance);
    let amountWithPrecision = amount;

    if (Number(walletBalance) >= Number(maxTradeAmount)) {
      amount = String(maxTradeAmount);
    }
    if (orderSide === OrderSide.Sell && currency === 'AVAX') {
      amount -= Number(networkFee);
      amount -= 0.05;
    }
    if (amount < 0) {
      amount = walletBalance;
    }
    if (currency === instrument?.base_currency_id) {
      amountWithPrecision = valueWithPrecision(amount, precision.base);
    } else {
      amountWithPrecision = valueWithPrecision(amount, precision.quote);
    }
    setCurrentCurrency(amountWithPrecision);
    handlerChangeAmount(amountWithPrecision);
  };

  const walletBalanceWithPrecision = () => {
    let walletWithPrecision: any = 0;
    const currentPrecision = orderSide === OrderSide.Sell ? precision.base : precision.quote;
    walletWithPrecision = valueWithPrecision(walletBalance, currentPrecision);
    return walletWithPrecision;
  };

  const addSymbol = (amount: any) => {
    const currentPrecision = orderSide === OrderSide.Sell ? precision.base : precision.quote;
    const zeroVsPrecission = valueWithPrecision(0, currentPrecision);
    const zeroVsPrecissionIncrease = zeroVsPrecission.substring(0, zeroVsPrecission.length - 1) + 1;
    if (Number(zeroVsPrecissionIncrease) > Number(amount)) return `<${zeroVsPrecissionIncrease}`;
    return amount;
  };

  return (
    <div className="create-base">
      <div className={`create-base-amount-box ${validationError ? 'error' : ''}`}>
        <span className="create-base-am">{_t('Amount', 'CREATE_ORDER.AMOUNT')} </span>
        {isMarket && (
          <div className="create-base-max">
            <span className="create-base-max-title" onClick={handlerMaxAmount}>
              {_t('MAX', 'CREATE_ORDER.MAX')}
            </span>
          </div>
        )}
        <input
          type="text"
          placeholder="0"
          value={currentCurrency}
          onChange={handlerChange}
          onFocus={handlerFocus}
          className="create-base-amount"
        />
        {isMarket && (
          <span className="create-base-currency">
            <i
              style={{ backgroundImage: `url(/assets/currency-icons/${currency}.svg)` }}
              className="create-base-currency-icon"
            />
            <span className="create-base-currency-title">{currency}</span>
          </span>
        )}
        {isLimit && (
          <Switcher
            list={switchList}
            selected={selectedCurrency}
            setSelect={setSelectCurrency}
            onEventClick={handlerChangeCurrency}
          />
        )}
      </div>

      <div className="create-base-details">
        {validationError && <p className="create-base-details-error">{validationError}</p>}

        <p className="create-base-details-acc-balance">
          <span className="create-base-details-title">{_t('Wallet Balance', 'CREATE_ORDER.WALLET_BALANCE')}</span>
          <span className="create-base-details-amount">
            {addSymbol(walletBalanceWithPrecision())} {currency}
          </span>
        </p>
        <p className="create-base-details-max-trade">
          <span className="create-base-details-title">{_t('Max Trade Size', 'CREATE_ORDER.MAX_TRADE')}</span>
          <span className="create-base-details-amount">
            {maxTradeAmount} {currency}
          </span>
        </p>
      </div>
    </div>
  );
};

export default CreateBase;
